
import { Component, Emit, Prop } from '@modules/vue-property-decorator/lib';
import CircleIcon from '@shared/components/container/circleIcon.vue';
import Container from '@shared/components/container/container.vue';
import CircleCheck from '@shared/components/form/circleCheck.vue';
import { BookingPaymentType } from '@shared/core/constants';
import { Format } from '@shared/core/helpers/formatHelper';
import { Booking } from '@shared/core/interfaces/booking';
import { Settings } from '@shared/core/services/configService';
import { CrudService } from '@shared/core/services/crudService';
import { ShopService } from '@shared/core/services/shopService';
import store from '@shared/core/store';
import { PriceDescriptor } from '@shared/core/types/PriceDescriptor';
import Vue from 'vue';
import { Loader } from '@shared/core/helpers/loaderHelper';

@Component({
  components: {
    Container,
    CircleCheck,
    CircleIcon,
  },
})
export default class CheckoutBookingPaymentTypes extends Vue {
  @Prop() private owner: any;
  @Prop({ required: true, default: [] }) private players!: any[];
  @Prop({ required: true }) private booking!: Booking;
  @Prop({ required: false, default: null }) private blocks!: any;
  @Prop({ required: false, default: false }) private isMatch!: boolean;
  private error: string = "";

  private readonly Format = Format;
  private readonly BookingPaymentType = BookingPaymentType;
  private paymentType: BookingPaymentType = BookingPaymentType.Full;
  private allowSharedPayment: boolean = false;
  private allowFullPayment: boolean = true;
  private allowPendingPayment: boolean = false;
  private sharedPaymentWithRetention: boolean = true;

  private genericPrice: PriceDescriptor | null = null;
  private showTooltip: boolean = false;

  private get isDarkMode() {
    return store.state.settings.DarkMode === true;
  }
  // Getters
  private get bookingStrPrice(): string {
    return Format.formatCurrency(this.booking.price.amount.toString());
  }

  private get ownerPrice(): string {
    return this.players[0].price;
  }

  private get ownerStrPrice(): string {
    return Format.formatCurrency(this.ownerPrice);
  }

  private get sharedStrPrice(): string {
    if (this.sharedPaymentWithRetention === false) {
      return this.ownerStrPrice;
    }
    const players =
      (this.genericPrice?.amount || 0) *
      (this.booking.maxParticipants - 1);
    return Format.formatCurrency(players + this.ownerPrice);
  }

  private get pricesWithTaxes() {
    return store.state.settings.PriceWithTaxes;
  }
  private get paysWithRetention() {
    return this.paymentType === BookingPaymentType.Shared && this.sharedPaymentWithRetention === true;
  }

  // Lifecycle  
  async created() {
    await this.loadPaymentTypes();
  }

  // Instance methods
  private async loadPrice(customerId: number): Promise<PriceDescriptor> {
    const url = `${Settings.HostName}/api/publicbookings/loadprice`;

    const res = await new CrudService().httpPost(url, {
      startDate: this.booking.date,
      bookingTypeId: this.booking.bookingTypeId,
      duration: this.booking.duration,
      customerId: customerId,
      resourceId: this.blocks.find((y) => y.noOnline === false).resourceId,
    });

    if (Format.IsNull(res)) {
      store.commit("openGlobalError", this.$t("crash"));
      return { amount: 0, isSpecialPrice: false };
    }

    if (res.ok === false) {
      this.error = res.errorMsg;
      store.commit("openGlobalError", this.error);
      return { amount: 0, isSpecialPrice: false };
    }

    if (this.pricesWithTaxes) {
      return {
        amount: res.price.price / this.booking.maxParticipants,
        isSpecialPrice: res.price.isSpecialPrice,
      };
    }

    return {
      amount: res.price.netPrice / this.booking.maxParticipants,
      isSpecialPrice: res.price.isSpecialPrice,
    };
  }

  private async loadPaymentTypes() {
    const url = `${Settings.HostName}/api/shop/payment-mode-allowed`;
    const paymentMode = await new CrudService().httpGet(url);

    this.paymentType = BookingPaymentType.Full;
    this.allowPendingPayment = paymentMode?.pendingPaymentAllowed === true;
    this.allowSharedPayment = paymentMode?.sharedPaymentAllowed === true && !this.isMatch;
    this.allowFullPayment = true;
    this.sharedPaymentWithRetention = paymentMode?.sharedPaymentWithRetention === true;

    if (
      this.booking.price.isSpecialPrice &&
      paymentMode?.fullPaymentAllowedWithSpecialPrices === false
    ) {
      this.allowFullPayment = false;
    }

    // If the booking is for only one participant, only full payment makes sense
    // This overrides the previous fullPaymentAllowedWithSpecialPrices condition,
    // because that condition only applies when there are multiple participants
    // that might have different prices each
    if (this.booking.maxParticipants === 1) {
      this.allowSharedPayment = false;
    }
    if (
      !this.allowFullPayment &&
      !this.allowSharedPayment &&
      !this.allowPendingPayment
    ) {
      store.commit(
        "openGlobalError",
        this.$t("No payment options available") +
        "." +
        this.$t("Please contact the club")
      );
      return;
    }
    if (this.allowSharedPayment) {
      this.paymentType = BookingPaymentType.Shared;
    }
    if (this.allowSharedPayment) {
      this.genericPrice = await this.loadPrice(-1);
    }
  }
  private getImagePath(img: string) {
    if (img === null || img === undefined || img === "") {
      return store.state.settings.Uploads + "/" + store.state.settings.Mainlogo;
    }
    return store.state.settings.Uploads + "/" + img;
  }
  private async goToPay(event: MouseEvent) {
    this.error = "";
    let url = this.isMatch
      ? `${Settings.HostName}/api/publicbookings/byplace-prebook`
      : `${Settings.HostName}/api/publicbookings/prebook`;

    this.booking.paymentType = this.paymentType;

    const payBtn = event.target as Element;
    Loader.LoadingElement(payBtn);    
    const res = await new CrudService().httpPost(url, {
      booking: this.booking,
      blocks: this.blocks,
      customerIsParticipant: true,
      isMultiple: false,
      customerIds: this.players?.map(p => p.customerId),
      players: this.players,
    });
    Loader.UnloadingElement(payBtn);

    if (res === undefined || res === null) {
      console.error("Crash pre booking ");
      store.commit("openGlobalError", this.error);
      return;
    }

    if (res.ok === false) {
      if (res.model !== undefined && res.model !== null) {
        this.error = Object.values(res.model).join(", ");
        store.commit("openGlobalError", this.error);
        return;
      }
      this.error = res.errorMsg;
      store.commit("openGlobalError", this.error);
      return;
    }

    if (res.model.serviceFree === true) {
      return this.$router.push("/customer-bookings");
    }

    let sales = res.model;
    if (this.paysWithRetention === false) {
      sales = sales.filter((s: any) => s.customerId === this.owner.id);
    }

    var shop = new ShopService();
    for (const sale of sales) {
      sale.title = sale.description;
      sale.paymentType = this.paymentType;
      sale.withRetention = this.sharedPaymentWithRetention;
      sale.paymentDeadline = sale.paymentDeadline;
      sale.cancelUrl = `publicbookings/cancel/${sale.bookingId}/${this.owner.id}`;
      shop.addSaleToCart(sale, null);
    }

    this.$router.push("/checkout");
  }

  // Event handlers
  @Emit('addPlayersClick')
  private onAddPlayersClick(event: MouseEvent) {
    return;
  }
  @Emit('removePlayerClick')
  private onRemovePlayerClick(player: any) {
    return player;
  }
}
